import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { logger } from '../../utils/logger';

interface SocialNotification {
  id: string;
  mensagem: string;
  tipo_servico: string;
  frequencia: number;
  is_active: boolean;
}

interface NotificationConfig {
  position: string;
  display_time: number;
  pause_after_close: number;
  is_active: boolean;
}

interface NotificationType {
  type_name: string;
  min_interval: number;
  max_interval: number;
  is_active: boolean;
}

export default function SocialProofNotifications() {
  const [notifications, setNotifications] = useState<SocialNotification[]>([]);
  const [currentNotification, setCurrentNotification] = useState<SocialNotification | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [config, setConfig] = useState<NotificationConfig | null>(null);
  const [types, setTypes] = useState<NotificationType[]>([]);
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const { data, error } = await supabase
          .from('ibx_live_notifications_config')
          .select('*')
          .eq('is_active', true)
          .maybeSingle();

        if (error) throw error;
        if (data) setConfig(data);
      } catch (error) {
        logger.error('Erro ao carregar configuração:', error);
      }
    };

    loadConfig();
  }, []);

  useEffect(() => {
    const loadNotificationTypes = async () => {
      try {
        const { data, error } = await supabase
          .from('ibx_notification_types')
          .select('*')
          .eq('is_active', true);

        if (error) throw error;
        if (data) setTypes(data);
      } catch (error) {
        logger.error('Erro ao carregar tipos:', error);
      }
    };

    loadNotificationTypes();
  }, []);

  useEffect(() => {
    const loadNotifications = async () => {
      try {
        const { data, error } = await supabase
          .from('ibx_social_notifications')
          .select('*')
          .eq('is_active', true)
          .order('created_at', { ascending: false });

        if (error) throw error;
        if (data && data.length > 0) setNotifications(data);
      } catch (error) {
        logger.error('Erro ao carregar notificações:', error);
      }
    };

    loadNotifications();
  }, []);

  useEffect(() => {
    if (config && config.is_active && notifications.length > 0 && types.length > 0 && !isPaused) {
      startNotificationCycle();
    }
  }, [config, notifications, types, isPaused]);

  const startNotificationCycle = () => {
    if (notifications.length === 0 || !config) return;

    const showRandomNotification = () => {
      if (isPaused) return;

      const randomNotification = notifications[Math.floor(Math.random() * notifications.length)];
      
      // Encontrar o tipo correspondente para usar o intervalo correto
      const notificationType = types.find(t => 
        randomNotification.tipo_servico.toLowerCase().includes(t.type_name.toLowerCase().split(' ')[0])
      );

      setCurrentNotification(randomNotification);
      setIsVisible(true);
      setProgress(100);

      // Animação da barra de progresso
      const displayTime = config.display_time * 1000;
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          const newProgress = prev - (100 / (displayTime / 100));
          return newProgress > 0 ? newProgress : 0;
        });
      }, 100);

      // Esconder notificação após o tempo configurado
      setTimeout(() => {
        setIsVisible(false);
        clearInterval(progressInterval);
      }, displayTime);

      // Calcular próximo intervalo baseado no tipo
      let nextDelay = 15000; // padrão 15 segundos
      if (notificationType) {
        const minInterval = notificationType.min_interval * 1000;
        const maxInterval = notificationType.max_interval * 1000;
        nextDelay = Math.random() * (maxInterval - minInterval) + minInterval;
      }

      setTimeout(showRandomNotification, displayTime + nextDelay);
    };

    const initialDelay = Math.random() * 3000 + 2000;
    setTimeout(showRandomNotification, initialDelay);
  };

  const handleClose = () => {
    setIsVisible(false);
    setIsPaused(true);
    
    // Pausar por X minutos conforme configuração
    if (config) {
      const pauseTime = config.pause_after_close * 60 * 1000;
      setTimeout(() => {
        setIsPaused(false);
      }, pauseTime);
    }
  };

  if (!currentNotification || !isVisible || !config || !config.is_active) return null;

  // Determinar posição baseada na configuração
  const positionClasses = {
    'bottom-left': 'bottom-6 left-6',
    'bottom-right': 'bottom-6 right-6',
    'top-left': 'top-6 left-6',
    'top-right': 'top-6 right-6',
  };

  const positionClass = positionClasses[config.position as keyof typeof positionClasses] || 'bottom-6 left-6';

  return (
    <div
      className={`fixed ${positionClass} z-50 transition-all duration-500 ${
        isVisible ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0'
      }`}
    >
      <div className="bg-white rounded-xl shadow-2xl border border-gray-200 p-4 max-w-sm relative overflow-hidden">
        {/* Progress Bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gray-100">
          <div
            className="h-full bg-gradient-to-r from-teal-500 to-emerald-500 transition-all duration-100 ease-linear"
            style={{ width: `${progress}%` }}
          ></div>
        </div>

        <div className="flex items-start gap-3 mt-1">
          <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg">
            <i className="ri-checkbox-circle-fill text-white text-xl"></i>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-gray-900 font-semibold leading-relaxed">
              {currentNotification.mensagem}
            </p>
            <div className="flex items-center gap-2 mt-1.5">
              <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                <i className="ri-time-line"></i>
                Há poucos minutos
              </span>
              {currentNotification.tipo_servico && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-teal-50 text-teal-700 text-xs font-medium rounded-full">
                  <i className="ri-shield-check-line"></i>
                  {currentNotification.tipo_servico}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 transition-colors flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>
      </div>
    </div>
  );
}